// COPIED from ptc-core web/src/shell/NotificationBell.tsx @ 998eb0a (fleet staff notifications, spec v0.2 §8; core #408 took
// this repo's load-failure line), the fleet reference; pinned by notifications/notifications.conformance.test.tsx. The
// deliberate differences:
//   - `useTranslation` comes from this repo's src/i18n (CLAUDE.md: importing it is what initialises the instance);
//   - every item is a plain real link to its absolute target: an app event's own link, or a CRM item's page on the CRM
//     (`notificationTarget`), since neither is a route of this app. "See all" stays an in-app link to /notifications.
// SUP-03 — header bell: unread badge + dropdown feed of "someone moved your task" notifications.
//
// Deliberately a hand-rolled popover (button + positioned panel + outside-click dismiss) rather
// than a Fluent Menu: the list rows are custom two-line entries, and Fluent popup surfaces are
// hostile to jsdom tests (see the Fluent v9 dropdown/aria-label render-loop gotcha).
// Clicking an item marks it read and navigates to the task's surface (workbench / findings inbox
// / task detail — see notificationTarget).

import { useEffect, useRef, useState } from 'react';
import { EntityLink } from '../components/links/EntityLink';
import { Alert20Regular } from '@fluentui/react-icons';
import type { TFunction } from 'i18next';
import { useTranslation } from '../i18n';
import {
  notificationTarget,
  notificationText,
  useMarkNotificationRead,
  useNotifications,
  type NotificationItem,
} from '../api/notifications';

function timeAgo(iso: string, t: TFunction): string {
  const then = new Date(iso).getTime();
  if (Number.isNaN(then)) return '';
  const mins = Math.max(0, Math.round((Date.now() - then) / 60_000));
  if (mins < 1) return t('common.ago.justNow');
  if (mins < 60) return t('common.ago.minutes', { count: mins });
  const hours = Math.round(mins / 60);
  if (hours < 24) return t('common.ago.hours', { count: hours });
  return t('common.ago.days', { count: Math.round(hours / 24) });
}

export function NotificationBell() {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const rootRef = useRef<HTMLDivElement | null>(null);
  const { data, isError } = useNotifications();
  const markRead = useMarkNotificationRead();

  const unreadCount = data?.unreadCount ?? 0;
  const items = data?.items ?? [];

  // Outside-click + Escape dismiss.
  useEffect(() => {
    if (!open) return;
    function onPointerDown(e: MouseEvent) {
      if (rootRef.current && !rootRef.current.contains(e.target as Node)) setOpen(false);
    }
    function onKeyDown(e: KeyboardEvent) {
      if (e.key === 'Escape') setOpen(false);
    }
    document.addEventListener('mousedown', onPointerDown);
    document.addEventListener('keydown', onKeyDown);
    return () => {
      document.removeEventListener('mousedown', onPointerDown);
      document.removeEventListener('keydown', onKeyDown);
    };
  }, [open]);

  // Real links (2026-10-29): runs however the item is followed (click, Ctrl-click, middle-click).
  function onItemFollow(n: NotificationItem) {
    if (!n.readAt) markRead.mutate(n.id);
    setOpen(false);
  }

  return (
    <div ref={rootRef} className="relative">
      <button
        type="button"
        aria-label={unreadCount > 0 ? t('shell.notifications.titleUnread', { count: unreadCount }) : t('shell.notifications.title')}
        aria-expanded={open}
        onClick={() => setOpen((o) => !o)}
        className="relative flex items-center justify-center w-8 h-8 rounded hover:bg-slate-100 text-slate-600"
      >
        <Alert20Regular />
        {unreadCount > 0 && (
          <span
            data-testid="notification-badge"
            className="absolute -top-0.5 -right-0.5 min-w-[16px] h-4 px-1 rounded-full bg-red-600 text-white text-[10px] leading-4 text-center font-semibold"
          >
            {unreadCount > 99 ? '99+' : unreadCount}
          </span>
        )}
      </button>

      {open && (
        <div
          role="menu"
          aria-label={t('shell.notifications.title')}
          className="absolute right-0 top-10 z-50 w-96 max-h-[28rem] overflow-y-auto rounded-md border border-slate-200 bg-white shadow-lg"
        >
          <div className="px-4 py-2 border-b border-slate-100 text-sm font-semibold text-slate-700">
            {t('shell.notifications.title')}
          </div>
          {isError && !data ? (
            // A feed that failed to load is said to have failed, never shown as an empty one (fleet: no silent fallbacks;
            // from supervision's PR #274, 2026-11-02).
            <div role="alert" className="px-4 py-6 text-sm text-red-700">{t('shell.notifications.page.loadError')}</div>
          ) : items.length === 0 ? (
            <div className="px-4 py-6 text-sm text-slate-500">{t('shell.notifications.empty')}</div>
          ) : (
            <ul className="divide-y divide-slate-100">
              {items.map((n) => {
                const rowClass = `w-full text-left px-4 py-2.5 hover:bg-slate-50 flex gap-2 items-start ${
                  n.readAt ? 'text-slate-500' : 'text-slate-800'
                }`;
                const body = (
                  <>
                    <span
                      aria-hidden
                      className={`mt-1.5 w-2 h-2 rounded-full shrink-0 ${
                        n.readAt ? 'bg-transparent' : (n.priority ?? 0) > 0 ? 'bg-red-600' : 'bg-blue-500'
                      }`}
                    />
                    <span className="min-w-0">
                      <span className={`block text-sm truncate ${n.readAt ? '' : 'font-medium'}`}>
                        {(n.priority ?? 0) > 0 && (
                          <span className="mr-1 text-xs font-semibold text-red-700">{t('shell.notifications.page.high')}</span>
                        )}
                        {notificationText(n, t)}
                      </span>
                      <span className="block text-xs text-slate-400">{timeAgo(n.createdAt, t)}</span>
                    </span>
                  </>
                );
                return (
                  <li key={n.id}>
                    {/* Another app's page (an app event, or the CRM's own item): a plain real link to it. An app
                        event's link carries ?n=, so the destination marks it read too; following it here marks it read
                        at once. */}
                    <a href={notificationTarget(n)} role="menuitem" onClick={() => onItemFollow(n)} className={rowClass}>
                      {body}
                    </a>
                  </li>
                );
              })}
            </ul>
          )}
          <div className="px-4 py-2 border-t border-slate-100 text-sm">
            <EntityLink href="/notifications" onFollow={() => setOpen(false)} className="text-blue-700 hover:underline">
              {t('shell.notifications.seeAll')}
            </EntityLink>
          </div>
        </div>
      )}
    </div>
  );
}
